// src/components/MembershipCard.tsx
//
// Aura Plus membership card for the wallet screen — mirrors the app's wallet
// membership card + subscribe flow, using the SAME deployed backend contracts:
//   - get_subscription_offer({ p_currency, p_variant })  -> the offer + benefits
//   - get_boot_context({ p_user_id })                    -> membership state
//   - create-membership-order({ plan_id })               -> Razorpay order
//   - confirm-membership({ order_id })                   -> activate after pay
//
// We implement the "one-time" purchase path (a normal Razorpay Order, no autopay
// mandate), which the app itself notes captures synchronously and activates
// instantly/reliably. The card renders nothing unless membership is enabled and
// an offer exists for the user's currency/variant, so it's a no-op otherwise.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { amplitudeApp } from "../index";
import { supabase } from "../supabaseClient";
import { AnalyticsManager, trackEvent } from "../utils/analytics";
import { IconCrown, IconStarFilled } from "@tabler/icons-react";
import SubscribeSheet from "./SubscribeSheet";
import styles from "./MembershipCard.module.css";

// membership_exp_v1 arm (Amplitude), mirrors the app: hidden = no UI (holdout),
// onetime = pay-once Razorpay order, autopay = UPI mandate (not ported to web).
type MembershipArm = "hidden" | "autopay" | "onetime";
const MEMBERSHIP_ARMS: MembershipArm[] = ["hidden", "autopay", "onetime"];

interface Benefit {
  service_key: string;
  base_minor: number;
  member_minor: number;
  pct_off: number;
}

interface Offer {
  plan_id: string;
  name: string;
  interval: string; // 'month' | 'year'
  price_minor: number;
  currency: string;
  max_pct_off: number;
  benefits: Benefit[];
}

const SERVICE_LABELS: Record<string, string> = {
  chat_message: "Chat questions",
  voice_call_minute: "Voice calls",
  report_premium: "Premium reports",
  monthly_report: "Monthly report",
};

// Headline "Save up to X%": use the largest PARTIAL discount (0 < pct < 100) so
// a 100%-off freebie (e.g. monthly report) doesn't make it read "save 100%".
// Mirrors SubscriptionOffer.fromMap in the app.
function headlinePct(offer: Offer): number {
  const partials = offer.benefits
    .map((b) => b.pct_off)
    .filter((p) => p > 0 && p < 100);
  if (partials.length === 0) {
    return offer.max_pct_off >= 100 ? 50 : offer.max_pct_off;
  }
  return Math.max(...partials);
}

export default function MembershipCard() {
  const { user, currency, pricingVariant, refreshUserStatus } = useAuth();
  const navigate = useNavigate();
  const { formatPrice } = usePricing();

  const [offer, setOffer] = useState<Offer | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [arm, setArm] = useState<MembershipArm>("hidden");
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  // Collapsed by default — the wallet shows a slim teaser and only reveals the
  // full offer (benefits + CTA) when the user opens it, mirroring the app's
  // wallet membership card (compact teaser → subscribe sheet).
  const [expanded, setExpanded] = useState(false);

  // Resolve the membership_exp_v1 arm the app-way: sticky value in
  // users.membership_variant wins (money-safe — never jumps across sessions);
  // otherwise fetch from Amplitude and backfill. Any miss/error -> 'hidden'.
  const resolveArm = useCallback(async (): Promise<MembershipArm> => {
    if (!user) return "hidden";
    try {
      const { data: row } = await supabase
        .from("users")
        .select("membership_variant")
        .eq("id", user.id)
        .maybeSingle();
      const sticky = row?.membership_variant as MembershipArm | undefined;
      if (sticky && MEMBERSHIP_ARMS.includes(sticky)) return sticky;
    } catch (e) {
      console.error("[membership_exp_v1] sticky read failed:", e);
    }
    try {
      await amplitudeApp.experiment.fetch();
      const raw = amplitudeApp.experiment.variant("membership_exp_v1")?.value;
      const next = (MEMBERSHIP_ARMS as string[]).includes(raw || "")
        ? (raw as MembershipArm)
        : "hidden";
      // Backfill only a real arm so a transient miss can't lock the user out.
      if ((MEMBERSHIP_ARMS as string[]).includes(raw || "")) {
        supabase
          .from("users")
          .update({ membership_variant: next })
          .eq("id", user.id)
          .then(undefined, (e: any) =>
            console.error("[membership_exp_v1] backfill failed:", e),
          );
      }
      return next;
    } catch (e) {
      console.error("[membership_exp_v1] arm fetch failed:", e);
      return "hidden";
    }
  }, [user]);

  const loadMembership = useCallback(async () => {
    if (!user || !currency) {
      setLoading(false);
      return;
    }
    try {
      // Membership state (enabled + is_member) from the shared boot context.
      const { data: boot } = await supabase.rpc("get_boot_context", {
        p_user_id: user.id,
      });
      const membership = (boot as any)?.membership ?? {};
      const memberEnabled = membership.enabled === true;
      const member = membership.is_member === true;
      setEnabled(memberEnabled);
      setIsMember(member);

      // Experiment arm gates whether non-members see any membership UI.
      const resolvedArm = await resolveArm();
      setArm(resolvedArm);

      // The offer (price + per-service benefits) for this currency/variant —
      // needed for both purchase arms (onetime = pay-once, autopay = recurring).
      if (memberEnabled && !member && (resolvedArm === "onetime" || resolvedArm === "autopay")) {
        const { data: offerRaw } = await supabase.rpc(
          "get_subscription_offer",
          { p_currency: currency, p_variant: pricingVariant || "control" },
        );
        setOffer(
          offerRaw && typeof offerRaw === "object" ? (offerRaw as Offer) : null,
        );
      } else {
        setOffer(null);
      }
    } catch (e) {
      console.error("MembershipCard load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user, currency, pricingVariant, resolveArm]);

  useEffect(() => {
    loadMembership();
  }, [loadMembership]);

  const openRazorpay = (options: any) => {
    if (!(window as any).Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => new (window as any).Razorpay(options).open();
      document.body.appendChild(script);
    } else {
      new (window as any).Razorpay(options).open();
    }
  };

  // PayU (India) hosted checkout for the Aura Plus membership — website-only,
  // PRIMARY gateway. The server resolves the price, guards double-buy, and
  // returns a hosted-checkout form; payu-callback verifies + activates the
  // membership (mirroring confirm-membership) and redirects back. Throws so the
  // caller can fall back to Razorpay if PayU can't start (create-payu-payment
  // errors, is 'skip'ped by the admin toggle, or has no PayU price). If the user
  // is already a member the thrown error carries `.already` so we don't fall
  // back — we just tell them.
  const startPayuMembership = async (): Promise<void> => {
    const { data, error } = await supabase.functions.invoke("create-payu-payment", {
      body: {
        purpose: "membership",
        plan_id: offer!.plan_id,
        return_origin: window.location.origin,
      },
    });
    if (error) throw new Error(error.message || "payu_init_failed");
    if ((data as any)?.error === "already_subscribed") {
      const err: any = new Error("already_subscribed");
      err.already = true;
      throw err;
    }
    if (!data || (data as any).error || (data as any).skip) {
      throw new Error((data as any)?.reason || (data as any)?.error || "payu_skip");
    }
    if (!(data as any).payu_url || !(data as any).params) throw new Error("payu_bad_response");

    // Auto-submit a hidden form -> browser navigates to PayU's hosted page.
    const form = document.createElement("form");
    form.method = "POST";
    form.action = (data as any).payu_url;
    Object.entries((data as any).params).forEach(([k, v]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = k;
      input.value = String(v);
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  // Razorpay one-time membership order — the FALLBACK path (unchanged app flow).
  const startRazorpayMembership = async (): Promise<void> => {
    if (!offer) return;
    const { data, error } = await supabase.functions.invoke(
      "create-membership-order",
      { body: { plan_id: offer.plan_id } },
    );
    if (error) throw new Error(error.message || "Could not start checkout");
    if ((data as any)?.error === "already_subscribed") {
      toast("You're already a member.", { icon: "✅" });
      await loadMembership();
      return;
    }
    if (!data?.order) throw new Error("Could not start checkout");

      const order = data.order;
      openRazorpay({
        key: data.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: `Aura AI ${offer.name}`,
        description: `${offer.name} membership`,
        prefill: { email: user?.email },
        theme: { color: "#FF2E93" },
        handler: async (_resp: any) => {
          // Verify + activate server-side, then refresh local member state.
          try {
            const { data: confirm } = await supabase.functions.invoke(
              "confirm-membership",
              { body: { order_id: order.id } },
            );
            AnalyticsManager.logPurchase(
              order.id,
              order.amount,
              order.currency,
              [
                {
                  itemId: "aura_plus_membership",
                  itemName: offer.name,
                  price: order.amount,
                  quantity: 1,
                },
              ],
            );
            if ((confirm as any)?.activated === true) {
              await refreshUserStatus();
              await loadMembership();
              toast.success(`${offer.name} is now active!`);
            } else {
              // Activation still settling — refresh shortly.
              toast("Payment received, activating your membership…", {
                icon: "⏳",
              });
              setTimeout(() => loadMembership(), 3000);
            }
            // Land the user on chat, same as the PayU path.
            navigate("/chat");
          } catch (e: any) {
            console.error("confirm-membership failed:", e);
            toast("Payment received — activation is finishing up.", {
              icon: "⏳",
            });
            setTimeout(() => loadMembership(), 3000);
            navigate("/chat");
          }
        },
        modal: {
          ondismiss: () => setPurchasing(false),
        },
      });
  };

  // Entry point: try PayU first (primary), fall back to Razorpay on any failure.
  const handleSubscribe = async () => {
    if (!offer || purchasing) return;
    setPurchasing(true);
    trackEvent("Subscription Clicked", {
      plan_id: offer.plan_id,
      billing: "one_time",
      source: "wallet",
    });

    try {
      try {
        await startPayuMembership();
        return; // PayU redirect in progress — page is navigating away
      } catch (e: any) {
        if (e?.already) {
          toast("You're already a member.", { icon: "✅" });
          await loadMembership();
          return;
        }
        console.warn("[membership] PayU unavailable, falling back to Razorpay:", e);
      }
      await startRazorpayMembership();
    } catch (e: any) {
      toast.error(e.message || "Could not start checkout");
    } finally {
      setPurchasing(false);
    }
  };

  // Gating (mirrors the app's isMembershipEnabled = enabled && arm != 'hidden'):
  //  - never render while loading, or if membership isn't enabled for the user
  //  - 'hidden' arm (holdout) shows nothing
  //  - active members always see their status card
  //  - non-members only see the purchase card on the 'onetime' arm w/ an offer
  //    (autopay/UPI-mandate arm isn't ported to web yet)
  if (loading) return null;
  if (!enabled || arm === "hidden") return null;

  if (isMember) {
    return (
      <div className={`${styles.card} ${styles.memberCard}`}>
        <div className={styles.memberTop}>
          <span className={styles.memberBadge}>★ Aura Plus</span>
          <span className={styles.activePill}>
            <span className={styles.activeDot} /> Active
          </span>
        </div>
        <p className={styles.memberSub}>
          You're getting member rates on chat, calls and reports.
        </p>
      </div>
    );
  }

  if ((arm !== "onetime" && arm !== "autopay") || !offer) return null;

  const pct = headlinePct(offer);

  // Non-member: the app's GOLD "BEST VALUE" membership banner (the gold-gradient
  // footer that sits under the wallet rates card). Tapping opens the subscribe
  // bottom sheet. Mirrors wallet_screen.dart _ratesMembershipFooter.
  return (
    <>
      <button
        type="button"
        className={styles.goldBanner}
        onClick={() => setExpanded(true)}
      >
        <span className={styles.bestValuePill}>
          <IconStarFilled size={12} /> BEST VALUE
        </span>
        <span className={styles.goldRow}>
          <span className={styles.premiumIcon}>
            <IconCrown size={16} />
          </span>
          <span className={styles.goldText}>
            {pct > 0 ? (
              <>
                Save <b className={styles.goldPct}>up to {pct}%</b> on chats,
                calls and reports
              </>
            ) : (
              "Unlock member pricing"
            )}
          </span>
          <span className={styles.glassArrow}>›</span>
        </span>
      </button>
      <SubscribeSheet
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        source="wallet"
      />
    </>
  );
}
